import React, { useState } from "react";

import styled from "styled-components";

import { colors, fonts, fontSizes, transitions } from "@/styles/design-system";

import { AccountType, isSubscription, useAccountType } from "./AccountTypeContext";

const isDev = process.env.NODE_ENV === "development";

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;


const TabContainer = styled.div`
  display: flex;
  gap: 2px;
  padding: 2px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(186, 144, 92, 0.2);
  border-radius: 6px;
`;

const Tab = styled.button<{ $active: boolean }>`
  height: 28px;
  padding: 0 12px;
  border-radius: 6px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.xs};
  font-weight: 400;
  color: ${({ $active }) => ($active ? colors.text.primary : "rgba(251, 244, 236, 0.65)")};
  background: ${({ $active }) => ($active ? "rgba(255, 220, 168, 0.15)" : "transparent")};
  border: none;
  cursor: pointer;
  transition: all ${transitions.normal};
  white-space: nowrap;

  &:hover {
    color: ${colors.text.primary};
    background: ${({ $active }) => ($active ? "rgba(255, 220, 168, 0.15)" : "rgba(255, 255, 255, 0.06)")};
  }
`;

const TABS = [
  { type: AccountType.FREE, label: "Free" },
  { type: AccountType.SUBSCRIPTION, label: "Subscription" },
  { type: AccountType.MUSIC_CREATOR, label: "Creator" },
];

/** Read the current dev-only billing period override (mirrored to
    sessionStorage so it survives navigation). Used by every UI surface
    that needs to render monthly vs annual variants without a real Stripe
    subscription. Default = monthly. */
export function readDevBillingPeriod(): "monthly" | "annual" {
  if (typeof window === "undefined") return "monthly";
  return window.sessionStorage.getItem("dev-billing-period") === "annual" ? "annual" : "monthly";
}

const DevAccountSwitcher: React.FC = () => {
  if (!isDev) return null;

  const { accountType, setAccountType } = useAccountType();
  const [billingPeriod, setBillingPeriodLocal] = useState<"monthly" | "annual">(readDevBillingPeriod);
  const setBillingPeriod = (p: "monthly" | "annual") => {
    setBillingPeriodLocal(p);
    if (typeof window !== "undefined") {
      if (p === "annual") window.sessionStorage.setItem("dev-billing-period", "annual");
      else window.sessionStorage.removeItem("dev-billing-period");
      // Notify same-tab listeners — sessionStorage 'storage' event only
      // fires across tabs. Custom event keeps page-local consumers in sync.
      window.dispatchEvent(new Event("dev-billing-period-change"));
    }
  };

  return (
    <Wrapper>
      <TabContainer>
        {TABS.map(({ type, label }) => (
          <Tab key={type} $active={accountType === type} onClick={() => setAccountType(type)}>
            {label}
          </Tab>
        ))}
      </TabContainer>

      {/* Billing period toggle — only relevant when on a Subscription tier. */}
      {isSubscription(accountType) && (
        <TabContainer>
          {(["monthly", "annual"] as const).map((p) => (
            <Tab key={p} $active={billingPeriod === p} onClick={() => setBillingPeriod(p)}>
              {p === "monthly" ? "Monthly" : "Annual"}
            </Tab>
          ))}
        </TabContainer>
      )}
    </Wrapper>
  );
};

export default DevAccountSwitcher;
